<template>
    <ChannelCredentialsPanelCmp
        :channel="channel"
        :diagram-state="diagramState"
        :diagram-failed="!!lastTest && !lastTest.ok"
        :title="`Credenciais do ${channelName}`"
        :lead="`Use as credenciais gerais para todas as lojas ou defina credenciais próprias para uma loja. Antes de salvar, o Muven testa a conexão.`"
        :tutorial-link="linkTutorialCredentials"
        :tutorial-description="`Passo a passo para gerar as credenciais no ${channelName}.`">

        <div class="cred-scope" role="tablist" aria-label="Escopo das credenciais" @keydown="onScopeKeydown">
            <button type="button"
                v-for="option in scopeOptions"
                :key="option.id"
                :ref="`scope-${option.id}`"
                :id="`${formId}-tab-${option.id}`"
                role="tab"
                class="cred-scope-option"
                :class="{ 'is-active': scope === option.id }"
                :aria-selected="scope === option.id ? 'true' : 'false'"
                :aria-controls="formId"
                :tabindex="scope === option.id ? 0 : -1"
                :disabled="isBusy || retrievingCredentials"
                @click="changeScope(option.id)">
                <fa :icon="option.icon" />
                {{option.label}}
            </button>
        </div>

        <form :id="formId"
            class="cred-scope-panel"
            role="tabpanel"
            :aria-labelledby="`${formId}-tab-${scope}`"
            novalidate
            @submit.prevent="testAndSave(credentialsForm, () => saveCredentials(credentialsForm, chosenStore))">

            <div v-if="scope === 'store'" class="cred-store">
                <label class="cred-store-label" :for="`${formId}-store`">Loja</label>
                <multiselect
                    :id="`${formId}-store`"
                    :value="chosenStore"
                    :options="storeList"
                    :multiple="false"
                    :show-labels="false"
                    :allow-empty="false"
                    :disabled="isBusy || retrievingCredentials"
                    @input="onStoreChosen"
                    placeholder="Escolha a loja"
                    label="name"
                    track-by="id" />
                <p class="cred-store-note">
                    <fa icon="info-circle" aria-hidden="true" />
                    Os valores desta loja substituem as credenciais gerais. Campos vazios usam o valor geral.
                </p>
            </div>

            <!-- Troca de loja/escopo: esqueleto no lugar dos campos, sem cobrir a tela -->
            <div v-if="retrievingCredentials" class="cred-skeleton" aria-hidden="true">
                <span v-for="n in skeletonCount" :key="n" class="cred-skeleton-field">
                    <span class="cred-skeleton-label"></span>
                    <span class="cred-skeleton-input"></span>
                </span>
            </div>
            <p v-if="retrievingCredentials" class="sr-only" role="status">Carregando credenciais</p>

            <fieldset v-else class="cred-fields" :disabled="isBusy">
                <legend class="sr-only">Credenciais {{channelName}}</legend>

                <CredentialFieldCmp
                    v-for="(field, key) in credentialsForm"
                    :key="`${scope}-${chosenStore.id || 'geral'}-${key}`"
                    :ref="`field-${key}`"
                    :field-key="key"
                    :field="field"
                    :channel-enum="channel.enumChannel"
                    :submitted="submitted" />
            </fieldset>

            <ChannelCredentialsTestResultCmp
                ref="testResult"
                :last-test="lastTest"
                :saving="savingContent"
                save-anyway
                @review="focusFirstField()"
                @save-anyway="saveCredentials(credentialsForm, chosenStore)" />

            <WooCommerceIpBlockNoticeCmp
                class="cred-ip-notice"
                v-if="channel.isWooCommerce()"
                :blocked="ipBlockSuspected"
                :store-url="storeUrl" />

            <p class="cred-golive">
                <fa icon="info-circle" class="cred-golive-icon" aria-hidden="true" />
                <span>Ao salvar, o Muven começa a sincronizar com a loja. Salve quando produtos, categorias e marcas estiverem prontos para a integração.</span>
            </p>
        </form>

        <template v-slot:footer-start>
            <router-link :to="`/canais/${channel.id}/configuracoes`" class="btn btn-outline-secondary">
                <fa icon="arrow-left" />
                Voltar
            </router-link>

            <span class="cred-dirty" v-if="isDirty" role="status">
                <span class="cred-dirty-dot" aria-hidden="true"></span>
                Alterações não salvas
            </span>
        </template>

        <template v-slot:footer-end>
            <button type="button"
                class="btn btn-outline-secondary"
                :disabled="isBusy || retrievingCredentials"
                @click="testConnection(credentialsForm)">
                <fa icon="sync-alt" :spin="testingConnection" />
                {{testingConnection ? 'Testando conexão' : 'Testar conexão'}}
            </button>

            <button type="submit"
                class="btn btn-primary"
                :form="formId"
                :disabled="isBusy || retrievingCredentials">
                <fa icon="sync-alt" spin v-if="isBusy" />
                <fa icon="check" v-else />
                {{primaryLabel}}
            </button>
        </template>
    </ChannelCredentialsPanelCmp>
</template>

<script>
import { mapState } from 'vuex'
import { ChannelModel } from '@/channel/models/ChannelModel'
import channelService from '@/channel/services/ChannelService'
import { StoreModel } from '../../store/models/StoreModel'
import dynamicCredentialsDataParser from '@/channel/utils/DynamicCredentialsDataParser'
import WooCommerceIpBlockNoticeCmp from '@/channel/components/WooCommerceIpBlockNoticeCmp'
import ChannelCredentialsPanelCmp from '@/channel/components/ChannelCredentialsPanelCmp.vue'
import ChannelCredentialsTestResultCmp from '@/channel/components/ChannelCredentialsTestResultCmp.vue'
import CredentialFieldCmp from '@/channel/components/CredentialFieldCmp.vue'
import CredentialsTestAndSave from '@/channel/mixins/CredentialsTestAndSave'

const SCOPE_OPTIONS = [
    { id: 'general', label: 'Geral', icon: 'cogs' },
    { id: 'store', label: 'Por loja', icon: 'store-alt' },
]

export default {
    name: 'ChannelCredentialsByStoreCmp',
    components: {
        WooCommerceIpBlockNoticeCmp,
        ChannelCredentialsPanelCmp,
        ChannelCredentialsTestResultCmp,
        CredentialFieldCmp,
    },
    mixins: [
        CredentialsTestAndSave,
    ],
    // Vem do ChannelCredentialsWrapperView (modal "Descartar alterações?")
    inject: {
        confirmDiscardChanges: {
            default: () => () => Promise.resolve(true),
        },
    },
    props: {
        channel: {
            type: ChannelModel,
            default: new ChannelModel(),
            required: true,
        },
        linkTutorialCredentials: {
            required: true,
            type: String,
            default: '',
        },
    },
    data() {
        return {
            scopeOptions: SCOPE_OPTIONS,
            scope: 'general',
            retrievingCredentials: false,
            chosenStore: new StoreModel(),
            schema: {},
            credentials: {},
            credentialsForm: {},
            credentialsByStoreForm: {},
        }
    },
    methods: {
        async saveCredentials(credentialsForm, chosenStore) {
            this.savingContent = true

            const channelId = this.$route.params.id
            const credentials = dynamicCredentialsDataParser.mountCredentialsObjectFromForm(credentialsForm)

            try {
                if (!!chosenStore && chosenStore.id) {
                    await channelService.saveCredentialsByStore(channelId, chosenStore.id, credentials)
                } else {
                    await channelService.saveCredentials(channelId, credentials)
                }

                this.$floatingAlert.success({
                    message: `Credenciais salvas com sucesso!`,
                })

                this.markSaved()
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.savingContent = false
        },
        // Só testa; o resultado fica na tela
        async testConnection(credentialsForm) {
            await this.runTest(credentialsForm)
        },
        async selectStore(chosenStore) {
            this.chosenStore = chosenStore && chosenStore.id ? chosenStore : this.storeList[0]

            await this.retrieveCredentialsByStore(this.chosenStore)
        },
        async retrieveMainCredentials() {
            this.retrievingCredentials = true

            const channelId = this.$route.params.id

            try {
                this.schema = await channelService.getCredentialsSchema(channelId)
                this.credentials = await channelService.getCredentials(channelId)
                this.chosenStore = new StoreModel()

                this.credentialsForm = dynamicCredentialsDataParser.mergeCredentialsSchemaAndServerInformation(this.schema, this.credentials)
            } catch(e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.retrievingCredentials = false
        },
        async retrieveCredentialsByStore(chosenStore) {
            this.retrievingCredentials = true

            const channelId = this.$route.params.id

            try {
                this.schema = await channelService.getCredentialsSchema(channelId)
                this.credentials = await channelService.getCredentialsByStore(channelId, chosenStore.id)

                this.credentialsForm = dynamicCredentialsDataParser.mergeCredentialsSchemaAndServerInformation(this.schema, this.credentials)
            } catch(err) {
                this.$floatingAlert.error({
                    message: err.message || `Houve um problema no momento de obter as credenciais da loja ${chosenStore.name}`,
                })
            }

            this.retrievingCredentials = false
        },
        // Trocar de escopo/loja recarrega o form: pergunta antes de jogar fora o que foi digitado
        async canLeaveCurrentForm() {
            return !this.isDirty || await this.confirmDiscardChanges()
        },
        async changeScope(scope) {
            if (scope === this.scope || !(await this.canLeaveCurrentForm())) {
                return
            }

            this.scope = scope

            if (scope === 'general') {
                await this.retrieveMainCredentials()
                return
            }

            await this.selectStore(this.chosenStore)
        },
        async onStoreChosen(store) {
            if (!store || (store.id === this.chosenStore.id) || !(await this.canLeaveCurrentForm())) {
                return
            }

            await this.selectStore(store)
        },
        // Setas alternam as abas (padrão WAI-ARIA de tablist)
        onScopeKeydown(event) {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
                return
            }

            event.preventDefault()

            const ids = this.scopeOptions.map((option) => option.id)
            const current = ids.indexOf(this.scope)
            let next = current

            if (event.key === 'ArrowRight') {
                next = (current + 1) % ids.length
            } else if (event.key === 'ArrowLeft') {
                next = (current - 1 + ids.length) % ids.length
            } else {
                next = event.key === 'Home' ? 0 : ids.length - 1
            }

            const button = this.$refs[`scope-${ids[next]}`]
            const target = Array.isArray(button) ? button[0] : button

            target && target.focus()
            this.changeScope(ids[next])
        },
    },
    async mounted() {
        await this.retrieveMainCredentials()
    },
    computed: {
        storeUrl() {
            const urlField = this.credentialsForm.url
            return urlField && typeof urlField.value === 'string' ? urlField.value : ''
        },
        channelName() {
            return this.channel.getRealName()
        },
        formId() {
            return `cred-form-${this._uid}`
        },
        skeletonCount() {
            return Math.max(2, Object.keys(this.credentialsForm || {}).length)
        },
        primaryLabel() {
            if (this.testingConnection) {
                return 'Testando conexão'
            }

            if (this.savingContent) {
                return 'Salvando'
            }

            return this.scope === 'store' && this.chosenStore.name ? `Testar e salvar para ${this.chosenStore.name}` : 'Testar e salvar'
        },
        ...mapState({
            storeList: (state) => {
                return StoreModel.getStoreListWithIdAndName(state.storeList)
            },
        })
    },
}
</script>

<style lang="scss" scoped>
$ease-out: cubic-bezier(.22, 1, .36, 1);

/* ── Geral | Por loja ── */

.cred-scope {
    display: flex;
    gap: 4px;
    width: max-content;
    max-width: 100%;
    margin: 0 auto 24px;
    padding: 4px;
    border: 1px solid var(--border-subtle);
    border-radius: 12px;
    background: var(--surface-2);
}

.cred-scope-option {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 18px;
    border: 0;
    border-radius: 9px;
    background: transparent;
    color: var(--text-muted);
    font-weight: 700;
    cursor: pointer;
    transition: background-color .2s $ease-out, color .2s, box-shadow .2s;

    &:hover:not(:disabled) {
        color: var(--text-strong);
    }

    &.is-active {
        background: var(--surface-1);
        color: var(--brand);
        box-shadow: var(--shadow-sm);
    }

    &:focus-visible {
        outline: 2px solid var(--brand-soft);
        outline-offset: 1px;
    }

    &:disabled {
        cursor: not-allowed;
    }
}

.cred-store {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 24px;
    padding: 16px;
    border: 1px solid var(--border-subtle);
    border-radius: 12px;
    background: var(--surface-2);
}

.cred-store-label {
    margin: 0;
    color: var(--text-strong);
    font-size: 14px;
    font-weight: 700;
}

.cred-store-note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 4px 0 0;
    color: var(--text-muted);
    font-size: 13px;
    line-height: 1.45;

    svg {
        flex-shrink: 0;
        margin-top: 3px;
    }
}

/* ── Campos ── */

.cred-fields,
.cred-skeleton {
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
}

.cred-skeleton-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.cred-skeleton-label,
.cred-skeleton-input {
    border-radius: 8px;
    background: linear-gradient(90deg, var(--surface-3), var(--surface-2), var(--surface-3));
    background-size: 200% 100%;
    animation: cred-shimmer 1.2s ease-in-out infinite;
}

.cred-skeleton-label {
    width: 140px;
    height: 14px;
}

.cred-skeleton-input {
    height: 44px;
    border-radius: 10px;
}

.cred-ip-notice {
    margin-top: 20px;
}

.cred-golive {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 24px 0 0;
    padding-top: 18px;
    border-top: 1px solid var(--border-subtle);
    color: var(--text-muted);
    font-size: 13px;
    line-height: 1.5;
}

.cred-golive-icon {
    flex-shrink: 0;
    margin-top: 3px;
    color: var(--text-faint);
}

.cred-dirty {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--warning-fg);
    font-size: 13px;
    font-weight: 700;
}

.cred-dirty-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--warning-solid);
}

@keyframes cred-shimmer {
    from { background-position: 100% 0; }
    to   { background-position: -100% 0; }
}

@media (prefers-reduced-motion: reduce) {
    .cred-skeleton-label,
    .cred-skeleton-input {
        animation: none;
    }
}
</style>
